/**
 * askHuman(): pause an agent inside a tool's execute() until the person picks.
 *
 *   const choice = await askHuman({ question, options: [{id, label}] });
 *
 * Resolves with the chosen option id, `{ timedOut: true }`, or `{ dismissed: true }`
 * (Escape, only when `dismissible`). Rejects with AbortError if `signal` aborts.
 */

export interface AskOption {
  id: string;
  label: string;
  description?: string;
}

export interface AskParams {
  question: string;
  options: AskOption[];
  /** Optional context line shown under the question. */
  detail?: string;
  timeoutMs?: number;
  dismissible?: boolean;
  signal?: AbortSignal;
  /** Mount point; defaults to document.body. */
  container?: HTMLElement;
}

export type AskOutcome = string | { timedOut: true } | { dismissed: true };

let styleInjected = false;
function injectStyle() {
  if (styleInjected) return;
  styleInjected = true;
  const s = document.createElement('style');
  s.textContent = `
.wm-ask-backdrop{position:fixed;inset:0;background:var(--wm-scrim,rgba(0,0,0,.35));display:grid;place-items:center;z-index:1000;padding:1rem}
.wm-ask{background:var(--surface-1,#fff);color:var(--fg,#111);border:1px solid var(--border,#ddd);border-radius:var(--radius-l,14px);box-shadow:var(--shadow-l,0 20px 50px rgba(0,0,0,.25));width:min(28rem,100%);padding:1.25rem 1.25rem 1rem;font:inherit}
.wm-ask__eyebrow{font-size:.75rem;letter-spacing:.06em;text-transform:uppercase;color:var(--fg-muted,#666);margin:0 0 .35rem}
.wm-ask__q{font-size:1.05rem;font-weight:600;margin:0 0 .25rem;line-height:1.35}
.wm-ask__detail{margin:0 0 .9rem;color:var(--fg-muted,#666);font-size:.9rem}
.wm-ask__opts{list-style:none;margin:0;padding:0;display:grid;gap:.4rem}
.wm-ask__opt{display:block;width:100%;text-align:left;border:1px solid var(--border,#ddd);background:var(--surface-2,#f6f6f6);color:inherit;border-radius:var(--radius-m,10px);padding:.6rem .75rem;cursor:pointer;font:inherit}
.wm-ask__opt:hover{border-color:var(--accent,#3b6cff)}
.wm-ask__opt:focus-visible{outline:2px solid var(--accent,#3b6cff);outline-offset:2px}
.wm-ask__opt small{display:block;color:var(--fg-muted,#666);margin-top:.15rem}
.wm-ask__foot{display:flex;justify-content:space-between;align-items:center;margin-top:.8rem;font-size:.75rem;color:var(--fg-muted,#666)}
.wm-ask__foot kbd{font:inherit;border:1px solid var(--border,#ddd);border-radius:4px;padding:0 .3em}
`;
  document.head.appendChild(s);
}

let counter = 0;

export function askHuman(params: AskParams): Promise<AskOutcome> {
  injectStyle();
  const { question, options, detail, timeoutMs, dismissible = false, signal, container = document.body } = params;
  if (!options.length) return Promise.reject(new Error('askHuman: options must not be empty'));

  return new Promise<AskOutcome>((resolve, reject) => {
    if (signal?.aborted) return reject(signal.reason ?? new DOMException('Aborted', 'AbortError'));
    const id = `wm-ask-${++counter}`;
    const previouslyFocused = document.activeElement as HTMLElement | null;

    const backdrop = document.createElement('div');
    backdrop.className = 'wm-ask-backdrop';
    const card = document.createElement('div');
    card.className = 'wm-ask';
    card.setAttribute('role', 'dialog');
    card.setAttribute('aria-modal', 'true');
    card.setAttribute('aria-labelledby', `${id}-q`);
    if (detail) card.setAttribute('aria-describedby', `${id}-d`);

    const eyebrow = document.createElement('p');
    eyebrow.className = 'wm-ask__eyebrow';
    eyebrow.textContent = 'Agent is asking you';
    const q = document.createElement('h2');
    q.className = 'wm-ask__q';
    q.id = `${id}-q`;
    q.textContent = question;
    card.append(eyebrow, q);
    if (detail) {
      const d = document.createElement('p');
      d.className = 'wm-ask__detail';
      d.id = `${id}-d`;
      d.textContent = detail;
      card.append(d);
    }

    const list = document.createElement('ul');
    list.className = 'wm-ask__opts';
    const buttons: HTMLButtonElement[] = [];
    options.forEach((o, i) => {
      const li = document.createElement('li');
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'wm-ask__opt';
      b.dataset.optionId = o.id;
      b.textContent = o.label;
      if (i < 9) b.setAttribute('aria-keyshortcuts', String(i + 1));
      if (o.description) {
        const s = document.createElement('small');
        s.textContent = o.description;
        b.append(s);
      }
      b.addEventListener('click', () => finish(o.id));
      li.append(b);
      list.append(li);
      buttons.push(b);
    });
    card.append(list);

    const foot = document.createElement('div');
    foot.className = 'wm-ask__foot';
    foot.innerHTML = `<span><kbd>↑</kbd><kbd>↓</kbd> move · <kbd>Enter</kbd> choose${dismissible ? ' · <kbd>Esc</kbd> dismiss' : ''}</span>`;
    const timer = document.createElement('span');
    foot.append(timer);
    card.append(foot);
    backdrop.append(card);

    let timeoutHandle: number | undefined;
    let tickHandle: number | undefined;

    const cleanup = () => {
      if (timeoutHandle) clearTimeout(timeoutHandle);
      if (tickHandle) clearInterval(tickHandle);
      signal?.removeEventListener('abort', onAbort);
      document.removeEventListener('keydown', onKey, true);
      backdrop.remove();
      previouslyFocused?.focus?.();
    };
    const finish = (v: AskOutcome) => {
      cleanup();
      resolve(v);
    };
    const onAbort = () => {
      cleanup();
      reject(signal?.reason ?? new DOMException('Aborted', 'AbortError'));
    };
    const onKey = (e: KeyboardEvent) => {
      const idx = buttons.indexOf(document.activeElement as HTMLButtonElement);
      if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
        e.preventDefault();
        buttons[(idx + 1 + buttons.length) % buttons.length].focus();
      } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
        e.preventDefault();
        buttons[(idx - 1 + buttons.length) % buttons.length].focus();
      } else if (e.key === 'Tab') {
        // trap focus inside the card
        e.preventDefault();
        buttons[(idx + (e.shiftKey ? -1 : 1) + buttons.length) % buttons.length].focus();
      } else if (e.key === 'Escape' && dismissible) {
        e.preventDefault();
        finish({ dismissed: true });
      } else if (/^[1-9]$/.test(e.key)) {
        const b = buttons[Number(e.key) - 1];
        if (b) {
          e.preventDefault();
          finish(b.dataset.optionId!);
        }
      }
    };

    signal?.addEventListener('abort', onAbort, { once: true });
    document.addEventListener('keydown', onKey, true);
    container.append(backdrop);
    buttons[0].focus();

    if (timeoutMs && timeoutMs > 0) {
      const deadline = Date.now() + timeoutMs;
      const tick = () => {
        timer.textContent = `${Math.max(0, Math.ceil((deadline - Date.now()) / 1000))}s`;
      };
      tick();
      tickHandle = window.setInterval(tick, 500);
      timeoutHandle = window.setTimeout(() => finish({ timedOut: true }), timeoutMs);
    }
  });
}
